import * as Yup from "yup";
import { Formik } from "formik";
import { Fragment, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  MenuItem,
  Button,
  TextField,
  Paper,
  Box,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { useAxios } from "../../hooks";
import { OuterBox } from "../../components";

const PRIMARY_COLOR = "rgb(57, 54, 239)";

const ResendNotification = () => {
  const axios = useAxios({ baseURL: "mox_api_2" });

  const [status, setStatus] = useState(undefined);
  const [entityList, setEntityList] = useState([]);

  const initialValues = {
    first_name: "",
    last_name: "",
    email_id: "",
    phone_number: "",
    entity_id: "",
  };

  useEffect(() => {
    axios({
      url: "/entry/entry/entity-list?entity_type=1",
      disableRedirect: true,
      disableError: true,
    }).then((response) => {
      if (response.status) {
        setEntityList(response.data);
      } else {
        setEntityList([]);
      }
    });
  }, []);

  const StatusDisplay = ({ message, iconColor, onClick }) => (
    <Grid
      container
      display="flex"
      sx={{ flexDirection: "column", p: 4 }}
      alignItems="center"
      justifyContent="center"
    >
      <CheckCircleOutlineRoundedIcon
        sx={{
          fontSize: "80px",
          color: iconColor,
          mb: 3,
        }}
      />
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          textAlign: "center",
          textTransform: "uppercase",
          color: iconColor,
        }}
      >
        {message}
      </Typography>
      <Button
        variant="outlined"
        sx={{
          mt: 3,
          color: iconColor,
          border: `2px solid ${iconColor}`,
          borderRadius: "12px",
          px: 4,
          textTransform: "capitalize",
          "&:hover": {
            background: iconColor,
            color: "#fff",
          },
        }}
        onClick={onClick}
      >
        Next
      </Button>
    </Grid>
  );

  return (
    <OuterBox>
      <Paper
        elevation={6}
        sx={{
          p: 5,
          borderRadius: "16px",
          background: "#fff",
          border: "1px solid rgb(57, 54, 239)",
          maxWidth: "600px",
          mx: "auto",
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{
            fontWeight: 700,
            color: PRIMARY_COLOR,
            mb: 3,
            textTransform: "uppercase",
          }}
        >
          Resend Notification
        </Typography>

        {status === undefined ? (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(formData, { setSubmitting }) => {
              setSubmitting(true);
              axios({
                url: "/notification/notification/resend-notification",
                method: "POST",
                data: {
                  entity_type_id: 1,
                  phone_number: formData.phone_number,
                  email_id: formData.email_id,
                  first_name: formData.first_name,
                  last_name: formData.last_name,
                  entity_id: formData.entity_id,
                  notification_type: "INVITE",
                },
                disableRedirect: true,
              }).then((response) => {
                if (response.status) {
                  setStatus(true);
                } else {
                  setStatus(false);
                }
                setSubmitting(false);
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              isSubmitting,
            }) => (
              <Fragment>
                <Grid container spacing={3} mt={1}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="first_name"
                      value={values.first_name}
                      onChange={handleChange}
                      error={touched.first_name && Boolean(errors.first_name)}
                      helperText={touched.first_name && errors.first_name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="last_name"
                      value={values.last_name}
                      onChange={handleChange}
                      error={touched.last_name && Boolean(errors.last_name)}
                      helperText={touched.last_name && errors.last_name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email_id"
                      value={values.email_id}
                      onChange={handleChange}
                      error={touched.email_id && Boolean(errors.email_id)}
                      helperText={touched.email_id && errors.email_id}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone_number"
                      value={values.phone_number}
                      onChange={handleChange}
                      error={
                        touched.phone_number && Boolean(errors.phone_number)
                      }
                      helperText={touched.phone_number && errors.phone_number}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Entity"
                      name="entity_id"
                      value={values.entity_id}
                      onChange={handleChange}
                      error={touched.entity_id && Boolean(errors.entity_id)}
                      helperText={touched.entity_id && errors.entity_id}
                    >
                      {entityList.map((entity) => (
                        <MenuItem
                          key={entity.entity_id}
                          value={entity.entity_id}
                        >
                          {entity.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} display="flex" justifyContent="center">
                    <Button
                      variant="contained"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                      sx={{
                        background: PRIMARY_COLOR,
                        borderRadius: "12px",
                        px: 5,
                        py: 1.2,
                        fontWeight: 600,
                        "&:hover": {
                          background: "rgb(45, 42, 200)",
                        },
                      }}
                      fullWidth
                    >
                      <ArrowForwardIcon />
                    </Button>
                  </Grid>
                </Grid>
              </Fragment>
            )}
          </Formik>
        ) : status ? (
          <StatusDisplay
            message="Notification Sent"
            iconColor="rgb(41 128 31)"
            onClick={() => setStatus(undefined)}
          />
        ) : (
          <StatusDisplay
            message="Notification Failed"
            iconColor="red"
            onClick={() => setStatus(undefined)}
          />
        )}
      </Paper>
    </OuterBox>
  );
};

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email_id: Yup.string().email("Invalid email").required("Email is required"),
  phone_number: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]{10,15}$/, "Phone number must be 10–15 digits"),
  entity_id: Yup.string().required("Entity is required"),
});

export default ResendNotification;
